import useApiRequest from "@/lib/hooks/use-request";
import { useUser } from "@/lib/hooks/use-user";
import { formatCurrency } from "@/lib/hooks/utils";
import {
  ProvisionResponseDto,
  SaleDetailsResponseDto,
  SaleState,
  UpdateProvisionRequestDto,
} from "@/lib/interfaces/sales";
import { UserRole } from "@/lib/interfaces/user";
import { Button } from "@/ui-components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/ui-components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/ui-components/ui/form";
import { Input } from "@/ui-components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/ui-components/ui/table";
import { zodResolver } from "@hookform/resolvers/zod";
import { Pencil } from "lucide-react";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

const ProvisionManager = ({
  item,
  setReload,
}: {
  item: SaleDetailsResponseDto;
  setReload: Dispatch<SetStateAction<boolean>>;
}) => {
  const [selectedProvision, setSelectedProvision] = useState<ProvisionResponseDto>();
  const [expandedProvisionId, setExpandedProvisionId] = useState<string>();
  const { user } = useUser();

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-row justify-between items-center">
        <h2 className="text-2xl font-bold">Provisionen</h2>
      </div>
      {item.provisions.length > 0 ? (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Benutzer</TableHead>
              <TableHead>Posten (VKP)</TableHead>
              <TableHead>Provisionsbasis</TableHead>
              <TableHead>Provision</TableHead>
              <TableHead>Lead Provision/Kosten</TableHead>
              <TableHead>Profit / Verlust Differenz</TableHead>
              <TableHead>Gesamtprovision</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {item.provisions.map((provision) => (
              <>
                <TableRow
                  key={provision._id}
                  onClick={() => {
                    if (
                      (user?.role === UserRole.Admin || user?.role === UserRole.SuperAdmin) &&
                      item.state !== SaleState.Closed
                    ) {
                      setExpandedProvisionId(expandedProvisionId === provision._id ? undefined : provision._id);
                    }
                  }}
                  className={`transition-colors duration-300 ease-in-out ${
                    (user?.role === UserRole.Admin || user?.role === UserRole.SuperAdmin) &&
                    item.state !== SaleState.Closed
                      ? "cursor-pointer"
                      : ""
                  } ${expandedProvisionId === provision._id ? "bg-muted/50" : ""}`}
                >
                  <TableCell>
                    <div className="flex flex-col">
                      <span className="font-bold">{provision.belongsToUserFullName}</span>
                      <span className="text-sm text-muted-foreground italic">{provision.belongsToSalesRankTitle}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-row items-center gap-2">
                      <span className="text-sm text-muted-foreground">{provision.amount} ×</span>
                      <div className="flex flex-col">
                        <span className="font-bold">{provision.productTitle}</span>
                        <span className="text-sm text-muted-foreground italic">
                          {provision.productNetPrice ? formatCurrency(provision.productNetPrice) : "0,00 €"} (
                          {provision.netPrice ? formatCurrency(provision.netPrice) : "0,00 €"})
                        </span>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>{provision.provisionBase ? formatCurrency(provision.provisionBase) : "0,00 €"}</TableCell>
                  <TableCell>
                    <div className="flex flex-col">
                      <span className="">
                        {provision.provisionAmount ? formatCurrency(provision.provisionAmount) : "0,00 €"}
                      </span>
                      <span className="text-sm text-muted-foreground italic">{provision.provisionPercentage}%</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-col">
                      {provision.ownLeadProvision && (
                        <span className="font-bold text-green-600">{formatCurrency(provision.ownLeadProvision)}</span>
                      )}
                      {provision.leadCost && (
                        <span className="font-bold text-red-600">-{formatCurrency(provision.leadCost)}</span>
                      )}
                      {!provision.ownLeadProvision && !provision.leadCost && "-"}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-col">
                      {provision.saleWinDifference && (
                        <span className="font-bold text-green-600">
                          {formatCurrency(provision.saleWinDifference * 0.5)}
                        </span>
                      )}
                      {provision.saleLossDifference && (
                        <span className="font-bold text-red-600">{formatCurrency(provision.saleLossDifference)}</span>
                      )}
                      {!provision.saleWinDifference && !provision.saleLossDifference && "-"}
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className="font-bold">
                      {provision.totalProvisionAmount ? formatCurrency(provision.totalProvisionAmount) : "0,00 €"}
                    </span>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell colSpan={7} className="p-0">
                    <div
                      className={`overflow-hidden transition-[max-height,opacity] duration-300 ease-in-out ${
                        expandedProvisionId === provision._id ? "max-h-[500px] opacity-100" : "max-h-0 opacity-0"
                      }`}
                    >
                      <div className="bg-muted/50 py-4">
                        <div className="flex flex-col gap-4 px-4">
                          <div className="flex items-center gap-2">
                            <Button
                              size="sm"
                              variant="ghost"
                              className="p-0"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedProvision(provision);
                              }}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <h3 className="font-bold text-sm">Provisionsberechnung</h3>
                          </div>

                          <div className="grid grid-cols-3 gap-4 text-sm">
                            {/* Provision Base Calculation */}
                            <div>
                              <div className="font-medium mb-1">1. Provisionsbasis</div>
                              <div className="space-y-1">
                                {provision.isBasicSecurity ? (
                                  <>
                                    <div className="text-muted-foreground">
                                      Grundabsicherung: Produktpreis (Nettorohertrag) × Menge
                                    </div>
                                    <div>
                                      {formatCurrency(provision.productNetPrice)} × {provision.amount} =
                                      <span className="font-bold"> {formatCurrency(provision.provisionBase)}</span>
                                    </div>
                                  </>
                                ) : (
                                  <>
                                    <div className="text-muted-foreground">
                                      Zubehör: (Produktpreis + Installationskosten) × Menge
                                    </div>
                                    <div>
                                      (
                                      {provision.productNetPrice ? formatCurrency(provision.productNetPrice) : "0,00 €"}{" "}
                                      +{" "}
                                      {provision.productNetInstallationCost
                                        ? formatCurrency(provision.productNetInstallationCost)
                                        : "0,00 €"}
                                      ) × {provision.amount} =
                                      <span className="font-bold">
                                        {provision.provisionBase ? formatCurrency(provision.provisionBase) : "0,00 €"}
                                      </span>
                                    </div>
                                  </>
                                )}
                              </div>
                            </div>

                            {/* Provision Calculation */}
                            <div>
                              <div className="font-medium mb-1">2. Provision</div>
                              <div>
                                <div>
                                  {provision.provisionBase ? formatCurrency(provision.provisionBase) : "0,00 €"} ×{" "}
                                  {provision.provisionPercentage / 100} ({provision.provisionPercentage}%) =
                                  <span className="font-bold">
                                    {provision.provisionAmount ? formatCurrency(provision.provisionAmount) : "0,00 €"}
                                  </span>
                                </div>
                              </div>
                            </div>

                            {/* Additional Costs/Benefits */}
                            <div>
                              <div className="font-medium mb-1">3. Abzüge / Zusätze</div>
                              <div className="space-y-1">
                                {provision.ownLeadProvision && (
                                  <div className="text-green-600">
                                    + Eigenleadprovision:{" "}
                                    {provision.ownLeadProvision ? formatCurrency(provision.ownLeadProvision) : "0,00 €"}
                                  </div>
                                )}
                                {provision.leadCost && (
                                  <div className="text-red-600">
                                    - Leadpreis: {provision.leadCost ? formatCurrency(provision.leadCost) : "0,00 €"}
                                  </div>
                                )}
                                {provision.saleWinDifference && (
                                  <div className="text-green-600">
                                    <div>
                                      + Verkaufsgewinn (Differenz):{" "}
                                      {provision.saleWinDifference
                                        ? formatCurrency(provision.saleWinDifference)
                                        : "0,00 €"}
                                    </div>
                                    <div className="text-sm">
                                      → 50% Anteil:{" "}
                                      {provision.saleWinDifference
                                        ? formatCurrency(provision.saleWinDifference)
                                        : "0,00 €"}{" "}
                                      × 0.5 =
                                      <span className="font-bold">
                                        {" "}
                                        {provision.saleWinDifference
                                          ? formatCurrency(provision.saleWinDifference / 2)
                                          : "0,00 €"}
                                      </span>
                                    </div>
                                  </div>
                                )}
                                {provision.saleLossDifference && (
                                  <div className="text-red-600">
                                    - Verkaufsverlust (Differenz):{" "}
                                    {provision.saleLossDifference
                                      ? formatCurrency(Math.abs(provision.saleLossDifference))
                                      : "0,00 €"}
                                  </div>
                                )}
                                <div className="border-t pt-1 mt-2">
                                  <div className="font-medium">
                                    Summe:{" "}
                                    {formatCurrency(
                                      (provision.ownLeadProvision || 0) -
                                        (provision.leadCost || 0) +
                                        (provision.saleWinDifference ? provision.saleWinDifference / 2 : 0) +
                                        (provision.saleLossDifference || 0)
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Total */}
                          <div className="pt-2 pb-2 border-t">
                            <div className="font-medium">Gesamtprovision</div>
                            <div>
                              <span className="font-bold text-base">
                                {provision.totalProvisionAmount
                                  ? formatCurrency(provision.totalProvisionAmount)
                                  : "0,00 €"}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </TableCell>
                </TableRow>
              </>
            ))}
          </TableBody>
        </Table>
      ) : (
        <div className="flex flex-col items-center justify-center gap-2 mt-4">
          <p>
            Es hat noch keine Provisionen. Schliesse die Verkaufsposition ab um die Provisionen angezeigt zu bekommen.
          </p>
        </div>
      )}
      <ProvisionDialog
        id={item._id}
        item={selectedProvision}
        setSelectedProvision={setSelectedProvision}
        setReload={setReload}
      />
    </div>
  );
};

const ProvisionDialog = ({
  id,
  item,
  setSelectedProvision,
  setReload,
}: {
  id: string;
  item?: ProvisionResponseDto;
  setSelectedProvision: Dispatch<SetStateAction<ProvisionResponseDto | undefined>>;
  setReload: Dispatch<SetStateAction<boolean>>;
}) => {
  const [open, setOpen] = useState(item !== undefined);
  const { apiRequest } = useApiRequest();

  const formSchema = z.object({
    provisionPercentage: z.string().optional(),
    ownLeadProvision: z.string().optional(),
    leadCost: z.string().optional(),
  });

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      provisionPercentage: item?.provisionPercentage?.toString(),
      ownLeadProvision: item?.ownLeadProvision?.toString(),
      leadCost: item?.leadCost?.toString(),
    },
  });

  useEffect(() => {
    setOpen(item !== undefined);
  }, [item]);

  useEffect(() => {
    if (open && item) {
      form.reset({
        provisionPercentage: item.provisionPercentage.toString() || "0",
        ownLeadProvision: item.ownLeadProvision?.toString() || "0",
        leadCost: item.leadCost?.toString() || "0",
      });
    }
  }, [form, open, item]);

  const onSubmit = async (data: z.infer<typeof formSchema>) => {
    const req: UpdateProvisionRequestDto = {
      provisionPercentage:
        data.provisionPercentage !== undefined ? Number(data.provisionPercentage) : item?.provisionPercentage,
      ownLeadProvision: data.ownLeadProvision !== undefined ? Number(data.ownLeadProvision) : item?.ownLeadProvision,
      leadCost: data.leadCost !== undefined ? Number(data.leadCost) : item?.leadCost,
    };

    await apiRequest(`sales/${id}/provision/${item?._id}`, "PATCH", {
      body: req,
      toast: { toastText: "Provision erfolgreich bearbeitet" },
    });
    setReload((prev) => !prev);
    setOpen(false);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(open) => {
        if (!open) {
          setSelectedProvision(undefined);
        }
        setOpen(open);
      }}
    >
      <DialogContent className="sm:max-w-[425px]">
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-5 h-full">
            <DialogHeader>
              <DialogTitle>Provision "{item?.productTitle}" bearbeiten</DialogTitle>
            </DialogHeader>
            <div>
              <div className="flex flex-col gap-2">
                {item?.provisionBonusCalculationsEditable && (
                  <>
                    <FormField
                      control={form.control}
                      name="ownLeadProvision"
                      render={({ field }) => (
                        <FormItem className="flex flex-col gap-2">
                          <FormLabel className="text-[16px] font-bold">Eigener Lead Provision</FormLabel>
                          <FormControl>
                            <Input {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="leadCost"
                      render={({ field }) => (
                        <FormItem className="flex flex-col gap-2">
                          <FormLabel className="text-[16px] font-bold">Lead Kosten</FormLabel>
                          <FormControl>
                            <Input {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </>
                )}
                <FormField
                  control={form.control}
                  name="provisionPercentage"
                  render={({ field }) => (
                    <FormItem className="flex flex-col gap-2">
                      <FormLabel className="text-[16px] font-bold">Provision in Prozent</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>
            <DialogFooter className="flex gap-2 sm:justify-start">
              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={(e) => {
                  e.stopPropagation();
                  setOpen(false);
                }}
              >
                Abbrechen
              </Button>
              <Button type="submit" className="w-full">
                Speichern
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default ProvisionManager;
